import React, { PureComponent } from 'react';
import MapButton from './MapButton';
import { AiOutlinePlayCircle } from 'react-icons/ai';
import {
  Button,
  List,
  Popper,
  Paper,
  ListItem,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RiArrowDropDownLine } from 'react-icons/ri';
import { ZoneRecording } from '../../../db';
import { setStateAsync, getUserPosition, updateState, SetStateAsync } from '../../../utils';
import { alertSuccess, alertError } from '../../../alertDispatcher';
import { getCurrentMission, getCurrentSession } from '../../../dataModelHelpers';
import { dispatchMissionUpdated } from '../../../missionEvents';
import { dispatchCoresNeedUpdate, dispatchZonesUpdated } from '../../../mapEvents';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state/index';
import { onRobotMessage, removeRobotMessageCallback } from '../../../EventBus';
import { INBOUND_TOPICS } from '../../../types/rosTopics';
import { simplifyPolygon } from '../../../simplify';
import { RecordingCoords, RecordingDevice, RobotConnectionStatus } from '../../../types/types';
import { ZONE_RECORDING_TYPE, ZoneRecordingType, ZoneType } from '../../../db/ZoneTypesDatatype';
import { Coordinate } from 'ol/coordinate';
import { MdPause, MdPlayArrow, MdSave } from 'react-icons/md';
import { IoMdTrash } from 'react-icons/io';
import { RosPosition } from '../../../types/rosmsg';
import { isValidRecordingCoordinate } from '../../../coordinateValidation';

interface RecordZoneButtonProps {
  updateRecordingCoords: (recordingCoords: RecordingCoords) => void;
  recordingCoords: RecordingCoords;
  connectionStatus: RobotConnectionStatus;
}

interface RecordZoneButtonState {
  anchorEl: HTMLElement | null;
  containerOpen: boolean;
  currentRecordingId: number | undefined;
  zones: {
    [key: number]: {
      type: ZoneRecordingType;
      device: RecordingDevice;
    };
  };
  zoneType: ZoneRecordingType;
}

export default class RecordZoneButton extends PureComponent<RecordZoneButtonProps, RecordZoneButtonState> {
  SERIALIZABLE_STATE_VARIABLES: (keyof RecordZoneButtonState)[] = ['containerOpen'];
  tabletRecordTimeout: ReturnType<typeof setTimeout>;
  syncInterval: ReturnType<typeof setInterval> | null = null;
  /** Cached reference to avoid ZoneRecording.get() per tick — get()
   *  calls initializeState which overwrites #coordinates with the
   *  last-synced snapshot, losing in-flight pushCoordinate data. */
  activeRecording: InstanceType<typeof ZoneRecording> | null = null;
  setStateAsync: SetStateAsync;

  constructor(props: RecordZoneButtonProps) {
    super(props);

    this.state = {
      anchorEl: null,
      containerOpen: false,
      zoneType: ZONE_RECORDING_TYPE.COLLECTED_FIELD,
      zones: {},
      currentRecordingId: undefined,
    };

    this.toggleContainer = this.toggleContainer.bind(this);
    this.handleRobotPos = this.handleRobotPos.bind(this);
    this.selectZoneType = this.selectZoneType.bind(this);
    this.toggleRecord = this.toggleRecord.bind(this);
    this.deleteZone = this.deleteZone.bind(this);
    this.applyZone = this.applyZone.bind(this);
    this.continueZone = this.continueZone.bind(this);
    this.watchTabletLocation = this.watchTabletLocation.bind(this);

    this.setStateAsync = setStateAsync.bind(this);
  }

  async componentDidMount() {
    this.deserializeState();
    this.initZones();

    const anchorEl = document.getElementById('recordZoneToggle');
    this.setState({ anchorEl });
  }

  componentWillUnmount() {
    removeRobotMessageCallback(INBOUND_TOPICS.POSITION, this.handleRobotPos);

    clearTimeout(this.tabletRecordTimeout);

    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    // Final sync so in-flight coordinates aren't lost.
    const recording = ZoneRecording.get(this.state.currentRecordingId);
    recording?.syncCoordinates();

    this.serializeState();
  }

  /**
   * serialize the state into session
   */
  serializeState() {
    const serializedState = {};

    for (const stateKey of this.SERIALIZABLE_STATE_VARIABLES) {
      serializedState[stateKey] = this.state[stateKey];
    }

    localStorage.setItem('RecordZoneButtonState', JSON.stringify(serializedState));
  }

  /**
   * deserialize the state
   */
  deserializeState() {
    const serializedState = JSON.parse(localStorage.getItem('RecordZoneButtonState') ?? '{}');

    if (serializedState) {
      for (const stateKey of Object.keys(serializedState)) {
        //this.setState({ [stateKey]: serializedState[stateKey] });
        this.setState(updateState(stateKey as keyof RecordZoneButtonState, serializedState[stateKey]));
      }
    }
  }

  initZones() {
    const mission = getCurrentMission();
    const allZones = mission?.getZoneRecordings() ?? [];
    const zones = {};

    allZones.forEach((zoneRecording) => {
      zones[zoneRecording.instance_id] = {
        type: zoneRecording.type,
        device: zoneRecording.device,
      };
    });

    this.setState({ zones });
  }

  async watchTabletLocation() {
    if (!this.activeRecording) {
      return;
    }

    const position = await getUserPosition({ enableHighAccuracy: true, maximumAge: Infinity });
    const newCoord: Coordinate = [position[0], position[1]];

    if (!isValidRecordingCoordinate(newCoord)) {
      if (this.state.currentRecordingId !== undefined) {
        this.tabletRecordTimeout = setTimeout(this.watchTabletLocation, 1000);
      }

      return;
    }

    this.activeRecording.pushCoordinate(newCoord);
    this.props.updateRecordingCoords({
      id: this.activeRecording.instance_id,
      coords: this.activeRecording.coordinates,
    });

    if (this.state.currentRecordingId !== undefined) {
      this.tabletRecordTimeout = setTimeout(this.watchTabletLocation, 1000);
    }
  }

  async handleRobotPos({ hostname, msg }: { hostname: string; msg: RosPosition }) {
    const session = getCurrentSession();

    if (hostname !== session?.robot_hostname) {
      return;
    }

    if (!this.state.currentRecordingId) {
      return;
    }

    const coordinates: Coordinate = [msg.x, msg.y];

    if (!isValidRecordingCoordinate(coordinates)) {
      return;
    }

    if (!this.activeRecording) {
      return;
    }

    // Push directly — avoids O(N) array copy per tick and bypasses
    // the coordinates setter (no syncToDB at 10Hz). Coordinates are
    // synced once when the recording is applied.
    this.activeRecording.pushCoordinate(coordinates);
    this.props.updateRecordingCoords({
      id: this.activeRecording.instance_id,
      coords: this.activeRecording.coordinates,
    });
  }

  addZone(recording: ZoneRecording) {
    const zones = { ...this.state.zones };
    zones[recording.instance_id] = {
      type: recording.type,
      device: recording.device,
    };

    return zones;
  }

  removeZone(recording: ZoneRecording) {
    const zones = { ...this.state.zones };
    delete zones[recording.instance_id];

    return zones;
  }

  async toggleRecord() {
    const mission = getCurrentMission();

    if (!mission) {
      alertError('No mission loaded');

      return;
    }

    if (this.state.currentRecordingId === undefined) {
      this.props.updateRecordingCoords({ id: undefined, coords: [] });

      const currentRecording = await ZoneRecording.createRecording(this.state.zoneType, mission.instance_id);

      currentRecording.device = this.props.connectionStatus === 'connected' ? 'robot' : 'tablet';

      const zones = this.addZone(currentRecording);

      this.activeRecording = currentRecording;
      this.setState({ currentRecordingId: currentRecording.instance_id, zones });

      // Periodic sync every 5s so coordinates survive a crash without
      // the per-tick syncToDB overhead.
      this.syncInterval = setInterval(() => {
        currentRecording.syncCoordinates();
      }, 5000);

      if (this.props.connectionStatus === 'connected') {
        onRobotMessage(INBOUND_TOPICS.POSITION, this.handleRobotPos);
      } else {
        await this.watchTabletLocation();
        currentRecording.device = 'tablet';
      }
    } else {
      clearTimeout(this.tabletRecordTimeout);
      removeRobotMessageCallback(INBOUND_TOPICS.POSITION, this.handleRobotPos);

      if (this.syncInterval) {
        clearInterval(this.syncInterval);
        this.syncInterval = null;
      }

      const recordingZone = this.activeRecording;
      this.activeRecording = null;

      if (!recordingZone) {
        return;
      }

      recordingZone.coordinates = simplifyPolygon(recordingZone.coordinates, 0.01, false);
      // simplifyRingWithUTM closes the ring (last point = first point)
      // for polygon use. Strip the closing point for the trail display
      // so RecordZoneLayer doesn't draw a line back to the start.
      const displayCoords = recordingZone.coordinates;
      const first = displayCoords[0];
      const last = displayCoords[displayCoords.length - 1];
      const isClosed = first && last && first[0] === last[0] && first[1] === last[1];
      this.props.updateRecordingCoords({
        id: recordingZone.instance_id,
        coords: isClosed ? displayCoords.slice(0, -1) : displayCoords,
      });
      this.setState({ currentRecordingId: undefined });
    }
  }

  async continueZone(zoneId: number) {
    await this.setStateAsync({ currentRecordingId: zoneId });

    this.activeRecording = ZoneRecording.get(zoneId) ?? null;

    if (this.activeRecording) {
      // simplifyPolygon closes the ring (last = first). Strip the
      // closing point so new coordinates append after the last real
      // point, not after the duplicate start.
      const coords = this.activeRecording.coordinates;
      const first = coords[0];
      const last = coords[coords.length - 1];

      if (first && last && first[0] === last[0] && first[1] === last[1]) {
        coords.pop();
      }

      const recording = this.activeRecording;
      this.syncInterval = setInterval(() => recording.syncCoordinates(), 5000);
    }

    if (this.props.connectionStatus === 'connected') {
      onRobotMessage(INBOUND_TOPICS.POSITION, this.handleRobotPos);
    } else {
      this.watchTabletLocation();
    }
  }

  async applyZone(zoneId: number) {
    const mission = getCurrentMission();

    if (!mission) {
      alertError('Mission not found');

      return;
    }

    const recordingZone = ZoneRecording.get(zoneId);

    if (!recordingZone) {
      alertError('Zone not found');

      return;
    }

    const coords = recordingZone.coordinates.slice(); // zone recording arr copy
    // Deduplication
    const coordsMap: Array<string> = coords.map((coord) => JSON.stringify(coord));
    const coordsSet: Set<string> = new Set(coordsMap);
    const coordsArray = [...coordsSet].map((x) => JSON.parse(x));

    if (!coordsArray.length) {
      alertError('Boundary has no points!');

      return;
    }

    // Close ring - push first coord in array to guarentee a polygon
    coordsArray.push(coordsArray[0].slice());

    if (coordsArray.length <= 4) {
      alertError(`Boundary does not has enough points(minimum of 4)`);

      return;
    }

    if (mission.getJob()?.plot_mission && recordingZone.type === ZONE_RECORDING_TYPE.COLLECTED_FIELD) {
      // get field
      const originalField = mission.getZones().find((sel) => sel.zone_type === ZoneType.FIELD);

      if (!originalField) {
        alertError('Field not found');

        return;
      }

      const poly_id = originalField.getOuterZoneBoundarys()[0].poly_id;

      await mission.updateOrDeleteBoundary(poly_id, [coordsArray]);
      mission.plot_boundary_collected = true;
    } else {
      await mission.createNewRegularZone(recordingZone.type, [coordsArray]);
    }

    if (this.props.recordingCoords.id === recordingZone.instance_id) {
      this.props.updateRecordingCoords({ id: undefined, coords: [] });
    }

    recordingZone.dispose();

    const zones = this.removeZone(recordingZone);

    this.setState({ zones });

    alertSuccess('Successfully applied zone');

    dispatchMissionUpdated();
    // Applying a recorded zone either rewrites the field boundary
    // (updateOrDeleteBoundary — removes cores outside) or creates a
    // new regular zone. Both paths can invalidate existing cores —
    // full core rebuild is the safe choice.
    dispatchCoresNeedUpdate();
    // updateOrDeleteBoundary fires BOUNDARY_UPDATED internally, but
    // createNewRegularZone does not. Explicit zone signal.
    dispatchZonesUpdated();
  }

  async deleteZone(zoneId: number) {
    const zoneToDelete = ZoneRecording.get(zoneId);

    if (!zoneToDelete) {
      alertError('Zone not found');

      return;
    }

    if (this.props.recordingCoords.id === zoneToDelete.instance_id) {
      this.props.updateRecordingCoords({ id: undefined, coords: [] });
    }

    zoneToDelete.dispose();
    const zones = this.removeZone(zoneToDelete);
    this.setState({ zones });
  }

  toggleContainer(event) {
    this.setState({ containerOpen: !this.state.containerOpen });
  }

  selectZoneType(popupState, zoneType: ZoneRecordingType) {
    popupState.close();
    this.setState({ zoneType });
  }

  render() {
    return (
      <React.Fragment>
        <MapButton
          tooltip="Record Boundary"
          onClick={this.toggleContainer}
          toggled={this.state.containerOpen}
          id={'recordZoneToggle'}
        >
          <AiOutlinePlayCircle size={22} />
        </MapButton>
        <Popper anchorEl={this.state.anchorEl} open={this.state.containerOpen} style={{ zIndex: 2 }}>
          <Paper style={{ width: 550 }}>
            <Button
              variant="outlined"
              onClick={this.toggleRecord}
              size={'medium'}
              style={{ textTransform: 'capitalize', marginLeft: 10, marginTop: 10 }}
            >
              {this.state.currentRecordingId === undefined ? 'Start New Recording' : 'Stop Recording'}
            </Button>
            <PopupState variant="popover">
              {(popupState) => (
                <React.Fragment>
                  <Button
                    disabled={this.state.currentRecordingId !== undefined}
                    style={{ textTransform: 'capitalize', marginTop: 10 }}
                    {...bindTrigger(popupState)}
                    size={'medium'}
                  >
                    {this.state.zoneType === ZONE_RECORDING_TYPE.COLLECTED_FIELD ? 'collected' : this.state.zoneType}
                    <RiArrowDropDownLine />
                  </Button>
                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      value={'collected field'}
                      onClick={() => this.selectZoneType(popupState, ZONE_RECORDING_TYPE.COLLECTED_FIELD)}
                    >
                      Collected
                    </MenuItem>
                    <MenuItem
                      value={'unsafe'}
                      onClick={() => this.selectZoneType(popupState, ZONE_RECORDING_TYPE.UNSAFE)}
                    >
                      Unsafe
                    </MenuItem>
                    <MenuItem value={'slow'} onClick={() => this.selectZoneType(popupState, ZONE_RECORDING_TYPE.SLOW)}>
                      Slow
                    </MenuItem>
                  </Menu>
                </React.Fragment>
              )}
            </PopupState>
            <IconButton
              onClick={this.toggleContainer}
              style={{ color: 'black', position: 'absolute', right: 10, top: 5 }}
              size={'small'}
            >
              <CloseIcon fontSize={'medium'} />
            </IconButton>
            <List dense>
              <Divider />
              {Object.keys(this.state.zones).length ? (
                Object.keys(this.state.zones).map((zoneId) => {
                  const zoneIdNumber = parseInt(zoneId);
                  const isZoneBeingRecorded = this.state.currentRecordingId === zoneIdNumber;
                  const isAnyZoneBeingRecored = this.state.currentRecordingId !== undefined;

                  return (
                    <React.Fragment key={zoneId}>
                      <ListItem>
                        <Grid container size={12}>
                          <Grid size={4}>
                            <Typography variant="body1" style={{ textTransform: 'capitalize' }}>
                              Type: {this.state.zones[zoneId].type}
                              {'\n'}
                            </Typography>
                            <Typography variant="body1" style={{ textTransform: 'capitalize' }}>
                              Source: {this.state.zones[zoneId].device}
                            </Typography>
                          </Grid>
                          <Grid style={{ textAlign: 'end' }} size={8}>
                            {isZoneBeingRecorded ? (
                              <PauseButton toggleRecord={this.toggleRecord} />
                            ) : (
                              <ContinueButton
                                zoneIdNumber={zoneIdNumber}
                                continueZone={this.continueZone}
                                isAnyZoneBeingRecorded={isAnyZoneBeingRecored}
                              />
                            )}
                            <Button
                              style={{
                                textTransform: 'none',
                                color: this.state.currentRecordingId === undefined ? 'rgb(29, 120, 29)' : '',
                              }}
                              size={'large'}
                              color={'secondary'}
                              disabled={this.state.currentRecordingId !== undefined}
                              onClick={() => this.applyZone(zoneIdNumber)}
                            >
                              <MdSave size={40} />
                              Save
                            </Button>
                            <Button
                              style={{
                                textTransform: 'none',
                                color: this.state.currentRecordingId === undefined ? '#cc0000' : '',
                              }}
                              size={'large'}
                              disabled={this.state.currentRecordingId !== undefined}
                              onClick={() => this.deleteZone(zoneIdNumber)}
                            >
                              <IoMdTrash size={40} />
                              Delete
                            </Button>
                          </Grid>
                        </Grid>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  );
                })
              ) : (
                <ListItem style={{ justifyContent: 'center', alignContent: 'center', paddingTop: 10 }}>
                  <Typography variant="caption" style={{ fontStyle: 'italic' }}>
                    No active recordings
                  </Typography>
                </ListItem>
              )}
            </List>
          </Paper>
        </Popper>
      </React.Fragment>
    );
  }
}

const PauseButton = ({ toggleRecord }) => {
  return (
    <Button
      style={{ textTransform: 'none' }}
      size={'large'}
      color={'secondary'}
      onClick={async () => {
        await toggleRecord();
      }}
    >
      <>
        <MdPause size={40} />
        Pause
      </>
    </Button>
  );
};

const ContinueButton = ({ zoneIdNumber, continueZone, isAnyZoneBeingRecorded }) => {
  return (
    <Button
      style={{ textTransform: 'none' }}
      size={'large'}
      color={'secondary'}
      disabled={isAnyZoneBeingRecorded}
      onClick={async () => {
        await continueZone(zoneIdNumber);
      }}
    >
      <>
        <MdPlayArrow size={40} />
        Continue
      </>
    </Button>
  );
};
